import { useState, useEffect } from "react";

/**
 * Custom hook for managing success/error messages with auto-dismiss
 * @param {number} timeout - Timeout in milliseconds (default: 3000)
 */
export function useMessage(timeout = 3000) {
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [message, timeout]);

  const showSuccess = (msg) => {
    setMessage(msg);
    setMessageType("success");
  };

  const showError = (msg) => {
    setMessage(msg);
    setMessageType("error");
  };

  const showWarning = (msg) => {
    setMessage(msg);
    setMessageType("warning");
  };

  const showInfo = (msg) => {
    setMessage(msg);
    setMessageType("info");
  };

  const clearMessage = () => {
    setMessage("");
  };

  return {
    message,
    messageType,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearMessage,
  };
}

/**
 * Custom hook for managing dialog/modal state
 */
export function useDialog() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  const openDialog = (dialogData = null) => {
    setData(dialogData);
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setData(null);
  };

  return {
    open,
    data,
    openDialog,
    closeDialog,
  };
}

/**
 * Custom hook for managing filters state
 * @param {object} initialFilters - Initial filter values
 */
export function useFilters(initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const clearFilter = (key) => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  return {
    filters,
    updateFilter,
    updateFilters,
    resetFilters,
    clearFilter,
  };
}

/**
 * Custom hook for debounced search
 * @param {function} callback - Function to call after debounce
 * @param {number} delay - Delay in milliseconds (default: 500)
 */
export function useDebounce(callback, delay = 500) {
  const [timeoutId, setTimeoutId] = useState(null);

  const debouncedCallback = (value) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const id = setTimeout(() => {
      callback(value);
    }, delay);

    setTimeoutId(id);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return debouncedCallback;
}

/**
 * Custom hook for pagination
 * @param {number} initialPage - Initial page (default: 0)
 * @param {number} initialPageSize - Initial page size (default: 10)
 */
export function usePagination(initialPage = 0, initialPageSize = 10) {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const resetPagination = () => {
    setPage(initialPage);
    setPageSize(initialPageSize);
  };

  return {
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    resetPagination,
    setPage,
    setPageSize,
  };
}
